import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  connectStateResults,
} from 'react-instantsearch-dom';

// External components
import stickyFill from 'stickyfilljs';

// Common components
import { FontPreviewToolbar } from 'myfonts-font-preview-toolbar';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import Spinner from '../../common/Spinner/Spinner';

// App Config
import AppConfiguration from '../../../services/AppConfiguration';
import ImageService from '../../../services/ImageService';
import BackboneApi from '../../../services/BackboneApi';
import appRoutes from '../../../constants/appRoutes';
import appErrorMessages from '../../../constants/appErrors';
import {
  REQUEST_RESULTS_ERROR,
  BAD_RESULTS_ERROR,
  NO_ERROR,
} from '../../../constants/errorConstants';
import SessionManager from '../../../services/SessionManager';

// Services
import {
  makePromiseCancelable,
  formatURL,
  redirectToUrl,
} from '../../../services/AppUtils';
import ResultService from '../services/ResultService';

// Store actions
import {
  setResultData,
  setFontPreviewInputText,
  setFontPreviewSliderValue,
  setError,
  setResultRequestFlag,
  setResultsPageNumber,
} from '../../../actions/result';

// Result components
import ResultList from '../ResultList/ResultList';

// Component styles
import './resultContainer.scss';

/**
 * This container is linked to the store `result` and the `crop` key and it wraps the ResultList component.
 * - Update the result data once we have the data from the BE.
 * - Display a result list and the images saved in the store.
 * - Makes the top containers sticky.
 * @extends {Component}
 */
class ResultContainer extends React.Component {
  constructor(props) {
    super(props);

    /**
     * Instance of the App configuration data.
     * @type {Object}
     */
    this.appConfig = AppConfiguration.getConfig();

    /**
     * Instance of Backbone Api
     * @type {BackboneApi}
     */
    this.backboneApi = new BackboneApi();

    /**
     * Function that cancels a pending "getCropResults" operation / promise.
     * @type {Function|Null}
     */
    this.cancelGetCropResults = null;

    /**
     * Instance of the Result service. It will get the image crop data from the BE.
     * @type {ResultService}
     */
    this.resultService = new ResultService(this.appConfig);

    /**
     * Component initial state.
     * @type {Object}
     * @property {String}  resultListImageHeight This state handles the slider value of the font preview toolbar.
     *                                           It gets its value from the slider onChange callback and then updates
     *                                           the image list height. It is not tied to the store since it is a value
     *                                           that is not needed to be persisted, and also is executed in every
     *                                           slider onChange tick. The default range value is defined by a config
     *                                           value.
     * @property {Object}  loginModal            All the properties used by the login modal.
     * @property {Boolean} showDefaultText       This state defines if the default text is showed or not.
     */
    this.state = {
      resultListImageHeight: this.props.fontPreviewSliderValue,
      showDefaultText: false,
    };
    /* Define Algolia search client */
    this.searchClient = algoliasearch(
      this.appConfig.algolia.client,
      this.appConfig.algolia.key
    );

    // Context binding for function.
    this.updatelListImageSize = this.updatelListImageSize.bind(this);
    this.handleBackToImageClick = this.handleBackToImageClick.bind(this);
    this.renderErrorMessage = this.renderErrorMessage.bind(this);
    this.clearError = this.clearError.bind(this);
    this.getCropResults = this.getCropResults.bind(this);
    this.postToForum = this.postToForum.bind(this);
    this.generateStyleFilter = this.generateStyleFilter.bind(this);
  }

  /**
   * Before the component is mounted:
   * - It gets the crop result data from the BE.
   * - Reset font preview slider, text value and image height.
   * - Update the font preview input text with the recognized text and the result data.
   * - Reset the results page number (the new results list should start at page 1).
   */
  componentWillMount() {
    if (this.props.isNewResultRequestNeeded) {
      this.clearError();
      this.props.setFontPreviewInputText('');
      this.props.setFontPreviewSliderValue(this.appConfig.fontPreviewToolbar.defaultSliderValue);
      this.props.setResultsPageNumber(1);
      this.setState({
        resultListImageHeight: this.appConfig.fontPreviewToolbar.defaultSliderValue,
      });

      this.getCropResults()
        .then((results) => {
          /*
           * If the encrypted text key is empty, set show default text in true. Then the default text is passed to the
           * result list.
           */
          if (!results.data.key) {
            this.setState({
              showDefaultText: true,
            });
          }

          // If there are no results or if the crop box is vertical, display an error message
          if (!results.data.key || results.data.styleIds.length === 0 || this.props.verticalCropSelected) {
            this.props.setError(BAD_RESULTS_ERROR);
          }

          this.props.setResultData(results.data);
          this.props.setResultRequestFlag(false);
        })
        .catch((data) => {
          if (data.canceled) {
            // eslint-disable-next-line no-console
            console.warn('Promise Canceled');
          } else {
            this.props.setError(REQUEST_RESULTS_ERROR);
            this.props.setResultRequestFlag(false);
          }
        });
    }
  }

  /**
   * When the component is mounted, it will add the sticky elements to stickyFill library instance in order to
   * cover this behavior in the non supported browsers like IE. This library will not add the hack if the browser
   * supports the `position: sticky` property.
   */
  componentDidMount() {
    stickyFill.add(document.querySelectorAll('.wtfResultContainer_stickyElement'));

    const userCookie = SessionManager.getUserToken();
    if (userCookie) {
      const userToken = window.atob(userCookie);
      this.backboneApi.validateUserLogin(userToken)
        .then(() => {
          // console.log('login successful');
        })
        .catch(() => {
          // console.log('login failed', e);
          // TODO: Handle error here
        });
    }
  }

  /**
   * It will cancel a `getCropResults` pending request;
   */
  componentWillUnmount() {
    if (this.cancelGetCropResults) {
      this.cancelGetCropResults();
    }
  }

  /**
   * Returns the data from the results BE endpoint. It also sets a function in
   * `this.cancelGetCropResults` in order to cancel the main XHR promise while it's still
   * working.
   * @returns {Promise}
   */
  getCropResults() {
    if (this.cancelGetCropResults) {
      this.cancelGetCropResults();
    }
    const cropResultsPromise = this.resultService.getCropResults(
      this.props.croppedImageUrl,
      this.props.experimentData,
      this.props.md5,
      this.props.annotationIndex,
    );
    const cancelableCropResultsPromise = makePromiseCancelable(cropResultsPromise);

    // Make it available in the component so it can be canceled somewhere else
    this.cancelGetCropResults = () => {
      cancelableCropResultsPromise.cancel();
      this.cancelGetCropResults = null;
    };

    return cancelableCropResultsPromise;
  }

  /**
   * This method returns an object with all the `FontPreviewToolbar` proptypes the component needs.
   * @returns {Object}
   */
  getFontPreviewToolbarProps() {
    return {
      text: this.props.fontPreviewInputText,
      sliderValue: this.props.fontPreviewSliderValue,
      sliderMinRange: this.appConfig.fontPreviewToolbar.sliderMinRange,
      sliderMaxRange: this.appConfig.fontPreviewToolbar.sliderMaxRange,
      onChangeButtonClick: this.props.setFontPreviewInputText,
      onSliderChange: this.updatelListImageSize,
      onSliderMouseLeave: this.props.setFontPreviewSliderValue,
    };
  }

  /**
   * This method returns the default text for the result list.
   * @returns {String}
   */
  getDefaultText() {
    return this.state.showDefaultText ? this.appConfig.fontPreviewToolbar.noTextRecognizedDefaultValue : '';
  }

  /**
   * When the user clicks the back cta it navigates back to the `/crop` route.
   */
  handleBackToImageClick() {
    this.props.redirectToCrop();
  }

  /**
   * Callback executed on the `onSliderChange`. It will update the `resultListImageHeight` state.
   */
  updatelListImageSize(size) {
    this.setState({
      resultListImageHeight: size,
    });
  }

  /**
   * Clear store errorCode.
   */
  clearError() {
    this.props.setError(NO_ERROR);
  }

  /**
   * Post a cropped image to the WTF Forum or open the Login Modal if the user is not logged in.
   */
  postToForum() {
    const userCookie = SessionManager.getUserToken();
    if (userCookie) {
      // Retrieve the binary information of the cropped image
      ImageService.getImageBlobFromImageUrl(this.props.croppedImageUrl)
        // Get a session id from the cropped image. This is needed for posting a new thread on the forum
        .then(image => this.backboneApi.getImageSession(image))
        // Redirect the user to the WTF Forum using the session id to auto populate the image field in the new thread
        .then(response => this.redirectToNewForumThread(response.data.sessionID))
        .catch(() => {
          // TODO: Handle error here
        });
    } else {
      const siteUrlWTF = `${this.appConfig.shopify.url}/pages/whatthefont`;
      const auth0Endpoint = `${this.appConfig.auth0.domain}/authorize?response_type=code&client_id=${this.appConfig.auth0.clientId}&state=STATE&redirect_uri=${this.appConfig.auth0.redirectUrl}/api/handlelogin?final_uri=${siteUrlWTF}&audience=${this.appConfig.auth0.audienceApi}/&scope=openid%20profile%20email&ui_locales=en`;
      window.location.replace(auth0Endpoint);
    }
  }

  /**
   * Navigate to the WTF Forum to create a new thread
   * @param {String} sessionID The image session id needed for the auto completion of the forum form
   */
  redirectToNewForumThread(sessionID) {
    const url = formatURL(
      this.appConfig.wtfForum.newThreadUrl,
      { sessionID }
    );
    redirectToUrl(url);
  }

  /**
   * Prepare the Algolia filter query for all the style ids.
   * @param {Array}     styleIds  List of style ids.
   * @returns {String}  Algolia filter query.
   */
  generateStyleFilter(styleIds) {
    let styleList = '';
    styleIds.forEach((element, i) => {
      if (i === 0) {
        styleList = `included_fonts.myfonts_style_id:"${element}"`;
      } else {
        styleList += ` OR included_fonts.myfonts_style_id:"${element}"`;
      }
    });
    return styleList;
  }

  /**
   * @return If errorCode is presented, render the error message component, otherwise null.
   */
  renderErrorMessage() {
    let dataToRender = null;

    if (this.props.errorCode) {
      const errorMessage = appErrorMessages.result[this.props.errorCode] ?
        appErrorMessages.result[this.props.errorCode] :
        appErrorMessages.result.DEFAULT;

      dataToRender = (
        <div
          onClick={this.props.redirectToCrop}
          onKeyPress={this.props.redirectToCrop}
          role='button'
          tabIndex={0}
          className='wtfResultContainer_errorWrapper'
        >
          <ErrorMessage text={errorMessage} hideCloseButton />
        </div>
      );
    }

    return dataToRender;
  }

  /**
   * @return Render Full image container
   */
  renderFullImageContainer() {
    return (
      <div className='wtfResultContainer_imageContainer'>
        <div className='wtfResultContainer_stickyElement'>
          <img
            className='wtfResultContainer_image'
            alt='Full'
            src={this.props.imageUrl}
          />
          <button onClick={this.handleBackToImageClick} className='wtfResultContainer_backAction'>
            <img src={`${this.appConfig.cdnUrl.url}/left-arrow.png`} alt='Left arrow icon' />
            <span>Back to image</span>
          </button>
        </div>
      </div>
    );
  }

  /**
   * @return Render cropped image container.
   */
  renderCroppedImageContainer() {
    return (
      <div className='wtfResultContainer_cropImageContainer wtfResultContainer_stickyElement'>
        <img
          className='wtfResultContainer_cropImage'
          alt='Cropped'
          src={this.props.croppedImageUrl}
        />
        <FontPreviewToolbar {...this.getFontPreviewToolbarProps()} />
        { this.renderErrorMessage() }
      </div>
    );
  }

  /**
   * @return Render the result list container.
   */
  renderResultListContainer() {
    let dataToRender = null;

    const { indexName } = this.appConfig.algolia;
    if (this.props.isNewResultRequestNeeded) {
      dataToRender = (
        <div className='wtfResultContainer_spinner'>
          <Spinner />
        </div>
      );
    } else {
      const styleString = this.generateStyleFilter(this.props.resultData.styleIds);
      const Content = connectStateResults(({ searching }) =>
        (searching ?
          <div className='wtfResultContainer_spinner'>
            <Spinner />
          </div> : <span />));
      dataToRender = (
        <div>
          <InstantSearch
            indexName={indexName}
            searchClient={this.searchClient}
          >
            <Configure filters={styleString} hitsPerPage={100} />
            <Content />
            <ResultList
              styleList={this.props.resultData.styleIds}
              fontPreviewText={this.props.fontPreviewInputText || this.getDefaultText()}
              predictionText={this.props.resultData.text}
              imageSize={this.props.fontPreviewSliderValue}
              imageHeight={this.state.resultListImageHeight}
              startOnPage={this.props.resultsPageNumber}
              onPageChange={this.props.setResultsPageNumber}
            />
          </InstantSearch>
          <div className='wtfResultContainer_description'>
            <div className='wtfResultContainer_noMatchtext'>
              No good match?
              <button
                className='wtfResultContainer_wtfForumButton'
                onClick={() => { this.postToForum(); }}
              >
                Consult an expert from the community!
              </button>
            </div>
            <div className='wtfResultContainer_wtfForumText'>
              Submit your image to the
              <button
                className='wtfResultContainer_wtfForumButton'
                onClick={() => { this.postToForum(); }}
              >
                WhatTheFont Forum
              </button>
              to have your image viewed by font geeks the world over.
            </div>
          </div>
        </div>
      );
    }

    return dataToRender;
  }

  render() {
    return (
      <div className='wtfResultContainer'>
        {this.renderFullImageContainer()}
        <div className='wtfResultContainer_dataContainer'>
          {this.renderCroppedImageContainer()}
          {this.renderResultListContainer()}
        </div>
      </div>
    );
  }
}

/**
 * Set the ResultContainer component props default values.
 * @type {Object}
 */
ResultContainer.defaultProps = {
  experimentData: '{}',
  imageUrl: '',
  croppedImageUrl: '',
  md5: '',
  annotationIndex: '',
  fontPreviewInputText: '',
  fontPreviewSliderValue: '',
  resultData: {},
  errorCode: '',
  verticalCropSelected: false,
  isNewResultRequestNeeded: true,
  resultsPageNumber: 1,
  setResultData: () => {},
  setFontPreviewInputText: () => {},
  setFontPreviewSliderValue: () => {},
  setResultRequestFlag: () => {},
  redirectToCrop: () => {},
  setError: () => {},
  setResultsPageNumber: () => {},
};

/**
 * Declare the ResultContainer component PropTypes for better validation.
 * @type {Object}
 * @property {String}   imageUrl                  Full image url.
 * @property {Object}   croppedImageUrl           Cropped image url.
 * @property {String}   md5                       MD5 of cropped image font.
 * @property {Number}   annotationIndex           Annotation Index of cropped image.
 * @property {String}   fontPreviewInputText      Input text value that shows the font preview component.
 * @property {String}   fontPreviewSliderValue    It specifies the size of the font rendered.
 * @property {Object}   resultData                Result data of the cropped area. Required to show the result list.
 * @property {Boolean}  isNewResultRequestNeeded  Flag to tell if the component should attempt a new results request.
 * @property {Number}   resultsPageNumber         The page number shown by the results list.
 * @property {Function} setResultData             Callback that updates the crop area list data in the store.
 * @property {Function} setFontPreviewInputText   Callback that updates the font preview input text in the store.
 * @property {Function} setFontPreviewSliderValue Callback that updates the font preview slider value in the store.
 * @property {Function} setResultRequestFlag      Callback that updates the results request flag.
 * @property {Function} redirectToCrop            Callback that navigates back to the crop area.
 * @property {Function} setError                  Callback that sets an error code in the store.
 * @property {Function} setResultsPageNumber      Callback that sets the page number shown by the results list.
 */
ResultContainer.propTypes = {
  experimentData: PropTypes.string,
  imageUrl: PropTypes.string,
  croppedImageUrl: PropTypes.string,
  md5: PropTypes.string,
  annotationIndex: PropTypes.number,
  fontPreviewInputText: PropTypes.string,
  fontPreviewSliderValue: PropTypes.string,
  isNewResultRequestNeeded: PropTypes.bool,
  resultData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      fontStyle: PropTypes.shape({
        id: PropTypes.number,
        MD5Hash: PropTypes.string,
        name: PropTypes.string,
        url: PropTypes.string,
      }),
      foundry: PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
      }),
    })),
    key: PropTypes.string,
    styleIds: PropTypes.arrayOf(PropTypes.string),
    text: PropTypes.string,
  }),
  errorCode: PropTypes.string,
  verticalCropSelected: PropTypes.bool,
  setResultData: PropTypes.func,
  setFontPreviewInputText: PropTypes.func,
  setFontPreviewSliderValue: PropTypes.func,
  setResultRequestFlag: PropTypes.func,
  redirectToCrop: PropTypes.func,
  setError: PropTypes.func,
  resultsPageNumber: PropTypes.number,
  setResultsPageNumber: PropTypes.func,
};

/**
 * It dispatches store action and returns an object of actions that will be merged with component props.
 * @param   {Object} dispatch bind action creators.
 * @returns {Object}
 */
const mapDispatchToProps = dispatch => ({
  setResultData: resultData => dispatch(setResultData(resultData)),
  setFontPreviewInputText: inputData => dispatch(setFontPreviewInputText(inputData)),
  setFontPreviewSliderValue: inputData => dispatch(setFontPreviewSliderValue(inputData)),
  setResultRequestFlag: requestFlagValue => dispatch(setResultRequestFlag(requestFlagValue)),
  redirectToCrop: () => dispatch(push(appRoutes.crop)),
  setError: error => dispatch(setError(error)),
  setResultsPageNumber: resultsPageNumber => dispatch(setResultsPageNumber(resultsPageNumber)),
});

/**
 * Connect component with the store and actions.
 * @type {Object}
 */
export default connect(
  state => ({
    imageUrl: state.upload.imageUrl,
    md5: state.crop.md5,
    annotationIndex: state.crop.annotationIndex,
    croppedImageUrl: state.crop.croppedImageUrl,
    verticalCropSelected: state.crop.verticalCropSelected,
    resultData: state.result.resultData,
    fontPreviewInputText: state.result.fontPreviewInputText,
    fontPreviewSliderValue: state.result.fontPreviewSliderValue,
    errorCode: state.result.errorCode,
    isNewResultRequestNeeded: state.result.isNewResultRequestNeeded,
    resultsPageNumber: state.result.resultsPageNumber,
    experimentData: state.upload.experimentData,
  }),
  mapDispatchToProps
)(ResultContainer);



// WEBPACK FOOTER //
// ./src/components/Result/ResultContainer/ResultContainer.js